import React, { useMemo } from "react";
import {
  Anchor,
  Badge,
  Box,
  Checkbox,
  Divider,
  Group,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import moment from "moment";
import { useCalendar } from "../../../../../context/CalendarContext";
import { useSalon } from "../../../../../context/SalonContext";
// @ts-ignore - JS context module without types
import { useCalendarEvent } from "../../../../../context/CalendarEventContext";
import { Employee, SalonService } from "../../../../../types/salon";
import {
  ALL_APPOINTMENT_STATUSES,
  AppointmentStatus,
  DEFAULT_SELECTED_STATUSES,
  STATUS_COLORS,
} from "../../../../../types/calendar";
// @ts-ignore — staffUtils.js predates the TS migration
import { isBookableActiveEmployee } from "../../../../../utils/staffUtils";
import { toggleInSelection } from "./filterSelection";
import styles from "./FilterDropdown.module.css";

/**
 * Number of filter GROUPS currently constrained to a subset (staff / services /
 * status). Drives the count badge on the Filters pill. "All selected" for a
 * group is signalled by the `"all"` sentinel (staff/services) or a full status
 * set. Pure so the pill can render it without mounting the dropdown.
 */
export function countActiveFilterGroups(
  selectedArtists: string[],
  selectedServiceTypes: string[],
  selectedStatuses: AppointmentStatus[],
): number {
  let n = 0;
  if (!selectedArtists.includes("all")) n += 1;
  if (!selectedServiceTypes.includes("all")) n += 1;
  if (selectedStatuses.length < ALL_APPOINTMENT_STATUSES.length) n += 1;
  return n;
}

export type FilterCountWindow = "day" | "week" | "list";

interface FilterDropdownProps {
  /** Which view the toolbar is in — sets the per-staff count window: the
   *  viewed day (day + card views), the viewed week, or today onward (list
   *  view, which is today-anchored). */
  view: FilterCountWindow;
}

/**
 * Filters dropdown panel — the calendar's staff / service-type / status
 * filters. Reads and writes the CalendarContext filter state
 * (`selectedArtists`, `selectedServiceTypes`, `selectedStatuses`,
 * `workingToday`), which every view filters by.
 */
const FilterDropdown: React.FC<FilterDropdownProps> = ({ view }) => {
  const {
    selectedArtists,
    setSelectedArtists,
    selectedServiceTypes,
    setSelectedServiceTypes,
    selectedStatuses,
    setSelectedStatuses,
    selectedDate,
    dateRange,
    workingToday,
    setWorkingToday,
    workingStaffIds,
  } = useCalendar();

  const { employees, services, serviceTypeOptions, currentUser } =
    useSalon() as {
      employees: Employee[] | undefined;
      services: SalonService[] | undefined;
      serviceTypeOptions: string[] | undefined;
      currentUser: { id: string } | undefined;
    };

  const { events } = useCalendarEvent() as { events: any[] };

  // "Working today" is context-backed (CalendarContext) so it also hides the
  // calendar columns/events for staff not working on the viewed date.

  const activeEmployees = useMemo(
    () => (employees || []).filter(isBookableActiveEmployee),
    [employees],
  );

  const allStaffIds = useMemo(
    () => activeEmployees.map((e) => e.id),
    [activeEmployees],
  );

  const allTypes = useMemo(
    () => serviceTypeOptions || [],
    [serviceTypeOptions],
  );

  // service_type -> event color (first service of that type that declares one).
  const typeColor = useMemo(() => {
    const map: Record<string, string> = {};
    (services || []).forEach((s) => {
      if (s.service_type && s.default_calendar_event_color) {
        if (!map[s.service_type])
          map[s.service_type] = s.default_calendar_event_color;
      }
    });
    return map;
  }, [services]);

  // Per-staff appointment count for the current view window. Event start Dates
  // carry salon wall-clock as browser-local time, so a plain moment() compare
  // matches how the grid buckets them.
  const staffCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const inWindow = (d: Date): boolean => {
      const m = moment(d);
      if (view === "week") {
        return (
          m.isSameOrAfter(moment(dateRange.start), "day") &&
          m.isBefore(moment(dateRange.end), "day")
        );
      }
      if (view === "list") return m.isSameOrAfter(moment(), "day");
      return m.isSame(moment(selectedDate), "day");
    };
    (events || []).forEach((e) => {
      if (e.isTimeBlock || !e.resourceId || !e.start) return;
      if (!inWindow(e.start)) return;
      counts[e.resourceId] = (counts[e.resourceId] || 0) + 1;
    });
    return counts;
  }, [events, view, dateRange.start, dateRange.end, selectedDate]);

  // "Working today": show only staff with working hours on the viewed date
  // (their columns/events are likewise hidden on the calendar).
  const displayedStaff = workingToday
    ? activeEmployees.filter((e) => workingStaffIds.has(e.id))
    : activeEmployees;

  const isAllStaff = selectedArtists.includes("all");
  const isAllServices = selectedServiceTypes.includes("all");

  const selectedStaffCount = isAllStaff
    ? allStaffIds.length
    : activeEmployees.filter((e) => selectedArtists.includes(e.id)).length;

  const selectedTypeCount = isAllServices
    ? allTypes.length
    : allTypes.filter((t) => selectedServiceTypes.includes(t)).length;

  // --- Toggle handlers ("all" sentinel re-added once every item is on) ---
  const toggleStaff = (id: string) =>
    setSelectedArtists(toggleInSelection(selectedArtists, id, allStaffIds));

  const toggleType = (type: string) =>
    setSelectedServiceTypes(
      toggleInSelection(selectedServiceTypes, type, allTypes),
    );

  const toggleStatus = (status: AppointmentStatus) => {
    if (selectedStatuses.includes(status)) {
      setSelectedStatuses(selectedStatuses.filter((s) => s !== status));
    } else {
      setSelectedStatuses([...selectedStatuses, status]);
    }
  };

  const selectAllStaff = () => setSelectedArtists(["all", ...allStaffIds]);
  const clearAllServices = () => setSelectedServiceTypes([]);
  const selectAllStatuses = () =>
    setSelectedStatuses([...ALL_APPOINTMENT_STATUSES]);

  const resetAll = () => {
    setSelectedArtists(["all", ...allStaffIds]);
    setSelectedServiceTypes(["all", ...allTypes]);
    setSelectedStatuses([...DEFAULT_SELECTED_STATUSES]);
    setWorkingToday(false);
  };

  const servicesSummary = isAllServices
    ? "All services"
    : `${selectedTypeCount} of ${allTypes.length} services`;

  const columnHeader = (
    label: string,
    linkLabel: string,
    onClick: () => void,
  ) => (
    <Group justify="space-between" align="center" wrap="nowrap" mb={6}>
      <Text className={styles.microLabel}>{label}</Text>
      <Anchor
        component="button"
        type="button"
        c="teal"
        fz="sm"
        onClick={onClick}
      >
        {linkLabel}
      </Anchor>
    </Group>
  );

  return (
    <Box className={styles.panel}>
      <Group align="stretch" gap={0} wrap="nowrap" className={styles.columns}>
        {/* STAFF */}
        <Stack gap={4} className={styles.column}>
          {columnHeader("Staff", "Select all", selectAllStaff)}
          <Checkbox
            color="teal"
            size="sm"
            label="Working today"
            checked={workingToday}
            onChange={(e) => setWorkingToday(e.currentTarget.checked)}
            className={styles.workingOnly}
          />
          <Divider my={6} />
          <ScrollArea.Autosize mah={300} type="auto" scrollbarSize={6}>
            <Stack gap={11} pr={6}>
              {displayedStaff.map((emp) => {
                const isYou =
                  emp.isSelf || (currentUser && emp.id === currentUser.id);
                const count = staffCounts[emp.id] || 0;
                return (
                  <Group
                    key={emp.id}
                    justify="space-between"
                    wrap="nowrap"
                    gap="xs"
                    className={styles.row}
                  >
                    <Checkbox
                      color="teal"
                      size="sm"
                      checked={isAllStaff || selectedArtists.includes(emp.id)}
                      onChange={() => toggleStaff(emp.id)}
                      label={
                        <Text span className={styles.rowLabel}>
                          {emp.first_name} {emp.last_name}
                          {isYou ? " (You)" : ""}
                        </Text>
                      }
                    />
                    <Text fz="sm" c="dimmed" className={styles.count}>
                      {count > 0 ? count : "–"}
                    </Text>
                  </Group>
                );
              })}
              {displayedStaff.length === 0 && (
                <Text fz="xs" c="dimmed" py={4}>
                  No staff
                </Text>
              )}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>

        <Divider orientation="vertical" />

        {/* SERVICES */}
        <Stack gap={4} className={styles.column}>
          {columnHeader("Services", "Clear all", clearAllServices)}
          <ScrollArea.Autosize mah={320} type="auto" scrollbarSize={6}>
            <Stack gap={11} pr={6}>
              {allTypes.map((type) => (
                <Checkbox
                  key={type}
                  color="teal"
                  size="sm"
                  checked={isAllServices || selectedServiceTypes.includes(type)}
                  onChange={() => toggleType(type)}
                  label={
                    <Group gap={8} wrap="nowrap">
                      <Box
                        className={styles.dot}
                        style={{
                          backgroundColor: typeColor[type] || "#adb5bd",
                        }}
                      />
                      <Text span className={styles.rowLabel}>
                        {type}
                      </Text>
                    </Group>
                  }
                />
              ))}
              {allTypes.length === 0 && (
                <Text fz="xs" c="dimmed" py={4}>
                  No services
                </Text>
              )}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>

        <Divider orientation="vertical" />

        {/* STATUS */}
        <Stack gap={4} className={`${styles.column} ${styles.statusColumn}`}>
          {columnHeader("Status", "Select all", selectAllStatuses)}
          <Stack gap={11}>
            {ALL_APPOINTMENT_STATUSES.map((status) => {
              const checked = selectedStatuses.includes(status);
              return (
                <Group
                  key={status}
                  gap="sm"
                  wrap="nowrap"
                  className={styles.row}
                >
                  <Checkbox
                    color="teal"
                    size="sm"
                    aria-label={status}
                    checked={checked}
                    onChange={() => toggleStatus(status)}
                  />
                  <Badge
                    color={checked ? STATUS_COLORS[status] : "gray"}
                    variant="light"
                    size="sm"
                    className={`${styles.statusPill} ${
                      checked ? "" : styles.mutedPill
                    }`}
                  >
                    {status}
                  </Badge>
                </Group>
              );
            })}
          </Stack>
        </Stack>
      </Group>

      <Divider my={10} />

      {/* Footer */}
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text c="dimmed" className={styles.footerSummary}>
          {selectedStaffCount} of {allStaffIds.length} staff · {servicesSummary}{" "}
          · {selectedStatuses.length} of {ALL_APPOINTMENT_STATUSES.length}{" "}
          statuses
        </Text>
        <Anchor
          component="button"
          type="button"
          c="teal"
          fz="sm"
          onClick={resetAll}
        >
          Reset all
        </Anchor>
      </Group>
    </Box>
  );
};

export default FilterDropdown;
